import { useEffect, useState, useRef } from 'react';
import FullItineraryBuilder from './FullItineraryBuilder';
import { jsPDF } from 'jspdf';
import {
  collection, doc, onSnapshot,
  addDoc, setDoc, updateDoc, deleteDoc, getDoc, serverTimestamp, query, orderBy, limit,
} from 'firebase/firestore';
import { ref as storageRef, getBytes, getMetadata } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { totalTravellers } from '../utils/groupSize';
import {
  DEFAULT_SLOT_KEYS, addActivitySlot, canMoveLunch, daySlotKeys, moveLunch, newActivitySlotKey, removeSlot, slotLabel, slotType,
} from '../utils/skeletalLayout';

// ── Constants ────────────────────────────────────────────────────────────────

const PALETTE_ITEMS = [
  { label: 'Arrival Flight',   type: 'flight'   },
  { label: 'Departure Flight', type: 'flight'   },
  { label: 'Training Session', type: 'activity' },
  { label: 'Friendly Match',   type: 'activity' },
  { label: 'La Liga Match',    type: 'activity' },
  { label: 'Stadium Tour',     type: 'activity' },
  { label: 'Coaching Session', type: 'activity' },
  { label: 'Padel Session',    type: 'activity' },
  { label: 'Free Time',        type: 'activity' },
  { label: 'City Tour',        type: 'activity' },
  { label: 'Travel Day',       type: 'activity' },
  { label: 'Hotel Check In',   type: 'activity' },
  { label: 'Hotel Check Out',  type: 'activity' },
  { label: 'Pool / Beach',     type: 'activity' },
  { label: 'Evening Activity', type: 'activity' },
];

const APPROVAL = {
  draft:         { label: 'Draft',                   color: '#6B7280', bg: '#F3F4F6' },
  // pending_* only appear on skeletals sent under the old email sign-off
  pending_admin: { label: 'Sent for Sign-Off',        color: '#92400E', bg: '#FEF3C7' },
  pending_group: { label: 'Sent for Sign-Off',        color: '#92400E', bg: '#FEF3C7' },
  approved:      { label: 'Approved',                 color: '#065F46', bg: '#D1FAE5' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function numDays(dep, ret) {
  if (!dep || !ret) return 3;
  const diff = Math.round((new Date(ret) - new Date(dep)) / 86400000);
  return Math.max(1, Math.min((diff || 0) + 1, 15));
}

function dayLabel(dep, idx) {
  if (!dep) return `Day ${idx + 1}`;
  const d = new Date(dep);
  d.setDate(d.getDate() + idx);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function defaultGrid(nDays) {
  const g = {};
  for (let d = 0; d < nDays; d++) {
    DEFAULT_SLOT_KEYS.forEach((key) => {
      const type = slotType(key);
      if (type !== 'flight') g[`${d}_${key}`] = { label: slotLabel(key), type, fixed: true };
    });
  }
  return g;
}

function cellColors(type) {
  if (type === 'meal')     return { bg: '#FFFBEB', border: '#F59E0B', text: '#78350F' };
  if (type === 'flight')   return { bg: '#F0FDF4', border: '#22C55E', text: '#14532D' };
  if (type === 'activity') return { bg: '#EBF2FC', border: '#93C5FD', text: '#1A4A8A' };
  return { bg: '#F9FAFB', border: '#E5E7EB', text: '#374151' };
}

// ── Crest loader (module-level so PDF handlers can await it directly) ────────

function pathFromStorageUrl(url) {
  try {
    const m = url && url.match(/\/o\/(.+?)(?:\?|$)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

async function loadCrestForPdf(crestStoragePath, crestUrl) {
  const path = crestStoragePath || pathFromStorageUrl(crestUrl);
  if (!path) return null;
  const timeout = new Promise((res) => setTimeout(() => res(null), 5000));
  const load = async () => {
    try {
      const sRef = storageRef(storage, path);
      // getMetadata is optional — only used for MIME type; infer from path if it fails
      const [bytes, meta] = await Promise.all([
        getBytes(sRef),
        getMetadata(sRef).catch(() => ({ contentType: path.match(/\.jpe?g$/i) ? 'image/jpeg' : 'image/png' })),
      ]);
      const blob = new Blob([bytes], { type: meta.contentType || 'image/png' });
      const rawDataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = (e) => res(e.target.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });
      return await new Promise((res) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 200;
          const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight, 1));
          const canvas = document.createElement('canvas');
          canvas.width  = Math.round(img.naturalWidth  * scale);
          canvas.height = Math.round(img.naturalHeight * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          res(canvas.toDataURL('image/png'));
        };
        img.onerror = () => res(rawDataUrl);
        img.src = rawDataUrl;
      });
    } catch { return null; }
  };
  return Promise.race([load(), timeout]);
}

// ── PDF Generation ───────────────────────────────────────────────────────────

function generateSkeletalPdf(grid, nDays, request, approvalState, crestDataUrl, { returnBase64 = false, hiddenCells = {}, dayOrder = {} } = {}) {
  // jsPDF uses WinAnsi/CP1252 — replace chars that corrupt the text stream
  function pdfSafe(str) {
    return (str || '')
      .replace(/→/g, '>')
      .replace(/·/g, '-')   // middle dot confuses jsPDF's WinAnsi encoder
      .replace(/–/g, '-')   // en dash
      .replace(/—/g, '-')   // em dash
      .replace(/[^\x00-\x7F]/g, '');
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const pageH = 297;
  const mL = 14;
  const mR = 14;
  const cW = pageW - mL - mR;

  const sf = (r, g, b) => doc.setFillColor(r, g, b);
  const sd = (r, g, b) => doc.setDrawColor(r, g, b);
  const st = (r, g, b) => doc.setTextColor(r, g, b);

  const NAVY  = [13, 34, 64];
  const PB    = [26, 74, 138];
  const AMBER = [232, 160, 32];
  const WHITE = [255, 255, 255];
  const GRAY  = [107, 114, 128];
  const LGRAY = [229, 231, 235];
  const MGRAY = [156, 163, 175];

  // Header
  sf(...NAVY); doc.rect(0, 0, pageW, 34, 'F');
  sf(...AMBER); doc.rect(0, 31, pageW, 3, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); st(...WHITE);
  doc.text('MST Sports Tours', mL, 14);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); st(147, 197, 253);
  doc.text('Skeletal Itinerary', mL, 21);

  const tourName = request.tourName || (request.schoolName ? `${request.schoolName} Tour` : 'Tour');

  // Crest in header — always PNG (canvas-normalised in the loader)
  const crestSz = 22;
  if (crestDataUrl) {
    try { doc.addImage(crestDataUrl, 'PNG', pageW - mR - crestSz, 6, crestSz, crestSz); } catch (_) {}
  }
  const textMaxX = crestDataUrl ? pageW - mR - crestSz - 4 : pageW - mR;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); st(...WHITE);
  doc.text(tourName, textMaxX, 12, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); st(147, 197, 253);
  const dest = request.destination || request.country || '';
  const parts = totalTravellers(request) || request.numParticipants || '';
  if (dest || parts) doc.text(`${dest}${parts ? ` · ${parts} travellers` : ''}`, textMaxX, 18, { align: 'right' });

  const fmtD = (s) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  if (request.departureDate || request.returnDate) {
    doc.setFontSize(7.5); st(147, 197, 253);
    doc.text(`${fmtD(request.departureDate)} – ${fmtD(request.returnDate)}`, textMaxX, 24, { align: 'right' });
  }

  let y = 42;

  // Status
  const stateInfo = APPROVAL[approvalState] || APPROVAL.draft;
  const stateRgb = approvalState === 'approved' ? [6, 95, 70]
    : approvalState === 'pending_group' ? [26, 74, 138]
    : approvalState === 'pending_admin' ? [146, 64, 14]
    : [107, 114, 128];
  doc.setFontSize(7); st(...stateRgb); doc.setFont('helvetica', 'bold');
  doc.text(`STATUS: ${stateInfo.label.toUpperCase()}`, mL, y);
  y += 7;

  // Day columns — each day lists its own slots in order, since lunch can sit
  // at a different point per day and days can have extra activity slots.
  // Flight slots only show where a flight has been filled in.
  const days = Array.from({ length: nDays }, (_, i) => i);
  const columns = days.map((d) => daySlotKeys(dayOrder, grid, d).filter((key) =>
    !hiddenCells[`${d}_${key}`] && (slotType(key) !== 'flight' || grid[`${d}_${key}`])
  ));
  const nRows = Math.max(1, ...columns.map((c) => c.length));
  const dayColW = Math.min(38, cW / Math.max(nDays, 1));
  const tableW = dayColW * nDays;
  const tableX = (pageW - tableW) / 2;
  const headH = 12;
  const rowH = Math.min(12, (pageH - 18 - 8 - y - headH) / nRows);
  const tableTopY = y;

  // Day header
  sf(...NAVY); doc.rect(tableX, y, tableW, headH, 'F');
  days.forEach((d, i) => {
    const cx = tableX + i * dayColW + dayColW / 2;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); st(...WHITE);
    doc.text(`DAY ${d + 1}`, cx, y + 4.5, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
    doc.text(dayLabel(request.departureDate, d), cx, y + 9, { align: 'center' });
  });
  y += headH;

  const MEAL_FILL    = [255, 251, 235]; const MEAL_BDR  = [245, 158,  11];
  const FLIGHT_FILL  = [240, 253, 244]; const FLIGHT_BDR= [ 34, 197,  94];
  const ACT_FILL     = [235, 242, 252]; const ACT_BDR   = [147, 197, 253];
  const EMPTY_BDR    = [229, 231, 235];

  days.forEach((d, di) => {
    const cx = tableX + di * dayColW;
    columns[di].forEach((key, ri) => {
      const cy = y + ri * rowH;
      const cell = grid[`${d}_${key}`];
      const caption = slotLabel(key);
      if (cell) {
        const fill  = cell.type === 'meal' ? MEAL_FILL  : cell.type === 'flight' ? FLIGHT_FILL  : ACT_FILL;
        const bdr   = cell.type === 'meal' ? MEAL_BDR   : cell.type === 'flight' ? FLIGHT_BDR   : ACT_BDR;
        const tc    = cell.type === 'meal' ? [120,53,15]: cell.type === 'flight' ? [20,83,45]    : PB;
        sf(...fill); doc.roundedRect(cx + 1, cy + 1, dayColW - 2, rowH - 2, 1, 1, 'F');
        sd(...bdr);  doc.setLineWidth(0.3);
        doc.roundedRect(cx + 1, cy + 1, dayColW - 2, rowH - 2, 1, 1, 'S');
        // Slot caption (Morning, Afternoon…) above the activity, unless it repeats it
        const showCaption = cell.type !== 'flight' && caption.toLowerCase() !== String(cell.label).toLowerCase();
        if (showCaption) {
          doc.setFont('helvetica', 'normal'); doc.setFontSize(4.8); st(...GRAY);
          doc.text(pdfSafe(caption.toUpperCase()), cx + dayColW / 2, cy + 3.6, { align: 'center' });
        }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.3); st(...tc);
        const lines = doc.splitTextToSize(pdfSafe(cell.label), dayColW - 4).slice(0, 2);
        const ty = showCaption
          ? cy + (lines.length > 1 ? 6.4 : 7.6)
          : cy + rowH / 2 + (lines.length > 1 ? -0.4 : 1.8);
        doc.text(lines, cx + dayColW / 2, ty, { align: 'center', lineHeightFactor: 1.2 });
      } else {
        sd(...EMPTY_BDR); doc.setLineWidth(0.2);
        doc.setLineDashPattern([1, 1], 0);
        doc.roundedRect(cx + 1.5, cy + 1.5, dayColW - 3, rowH - 3, 1, 1, 'S');
        doc.setLineDashPattern([], 0);
        doc.setFont('helvetica', 'italic'); doc.setFontSize(5.5); st(...MGRAY);
        doc.text(pdfSafe(caption), cx + dayColW / 2, cy + rowH / 2 + 1.5, { align: 'center' });
      }
    });
    if (di > 0) { sd(...LGRAY); doc.setLineWidth(0.15); doc.line(cx, y, cx, y + nRows * rowH); }
  });

  sd(...NAVY); doc.setLineWidth(0.4);
  doc.rect(tableX, tableTopY, tableW, headH + nRows * rowH, 'S');
  y += nRows * rowH + 10;

  // Footer
  const fY = pageH - 18;
  sf(...NAVY); doc.rect(0, fY, pageW, 18, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); st(...WHITE);
  doc.text('MST Sports Tours', mL, fY + 7);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); st(147, 197, 253);
  doc.text('info@mstsportstours.com  ·  www.mstsportstours.com', mL, fY + 13);
  st(...MGRAY);
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · Confidential`,
    pageW - mR, fY + 10, { align: 'right' }
  );

  if (returnBase64) return doc.output('datauristring');
  doc.save(`${tourName.replace(/[^a-z0-9]/gi, '_')}_Skeletal_Itinerary.pdf`);
}

// ── Icons ────────────────────────────────────────────────────────────────────

function IconFlight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IconDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

// ── Mark as approved ─────────────────────────────────────────────────────────
// Sign-off happens with the coach away from the portal (usually the tour's
// WhatsApp group), so staff mark the skeletal approved themselves.

function MarkApprovedModal({ saving, onConfirm, onClose }) {
  const [note, setNote] = useState('');
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(13,34,64,0.45)', backdropFilter: 'blur(3px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[16px] w-full max-w-[460px] p-7" style={{ boxShadow: '0 24px 60px rgba(13,34,64,0.18)' }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-[18px] font-bold text-navy mb-1">Mark skeletal as approved</h2>
            <p className="text-[12.5px] text-[#6B7280]">Confirm the coach / group has approved this skeletal itinerary. This unlocks the full itinerary.</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] text-[22px] leading-none px-1">×</button>
        </div>
        <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
          Note <span className="text-[#9CA3AF] font-normal">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="e.g. Approved by the coach in the WhatsApp group on 28/10"
          className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-pb mb-4"
        />
        <div className="flex gap-2.5">
          <button
            onClick={() => onConfirm(note.trim())}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[13px] font-bold text-white bg-sg hover:opacity-90 rounded-[10px] transition disabled:opacity-60"
          >
            <IconCheck /> {saving ? 'Saving…' : 'Mark as approved'}
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 text-[13px] font-semibold text-[#374151] bg-white border border-[#E5E7EB] rounded-[10px] hover:bg-surface transition">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Skeletal Itinerary ───────────────────────────────────────────────────────

function SkeletalItinerary({ requestId, request, onApproved }) {
  const nDays = numDays(request.departureDate, request.returnDate);
  const [grid,          setGrid]          = useState(() => defaultGrid(nDays));
  const [hiddenCells,   setHiddenCells]   = useState({});
  const [dayOrder,      setDayOrder]      = useState({});
  const [approvalState, setApprovalState] = useState('draft');
  const [approval,      setApproval]      = useState(null); // { by, at, note }
  const [savedAt,       setSavedAt]       = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [saved,         setSaved]         = useState(false);
  const [dragOver,      setDragOver]      = useState(null);
  const [showApprove,   setShowApprove]   = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [flightsApplied, setFlightsApplied] = useState(false);
  const [paletteItems,  setPaletteItems]  = useState(PALETTE_ITEMS);
  const [templates,     setTemplates]     = useState([]);
  const [savingTemplate,  setSavingTemplate]  = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const { currentUser } = useAuth();
  const dragRef    = useRef(null);
  // Load palette items from Firestore; fall back to hardcoded defaults if none exist
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'skeletalPaletteItems'), orderBy('order')),
      (snap) => {
        if (snap.empty) {
          setPaletteItems(PALETTE_ITEMS);
        } else {
          setPaletteItems(snap.docs.map((d) => ({ ...d.data() })));
        }
      }
    );
    return unsub;
  }, []);

  // Load saved skeletal templates (shared across all tours)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'skeletalTemplates'), orderBy('publishedAt', 'desc'), limit(30)),
      (snap) => setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  // Load from Firestore (live)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'requests', requestId, 'itineraryMeta', 'skeletal'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.grid)          setGrid(data.grid);
        if (data.hiddenCells)   setHiddenCells(data.hiddenCells);
        setDayOrder(data.dayOrder || {});
        if (data.approvalState) setApprovalState(data.approvalState);
        setApproval(data.approvalState === 'approved' && data.approvedBy
          ? { by: data.approvedBy, at: data.approvedAt?.toDate?.() || null, note: data.approvalNote || '' }
          : null);
        if (data.savedAt?.toDate) setSavedAt(data.savedAt.toDate());
        if (data.flightsApplied) setFlightsApplied(true);
      } else {
        setGrid(defaultGrid(nDays));
      }
    });
    return unsub;
  }, [requestId, nDays]);

  const isLocked = approvalState === 'approved';

  // Writes the whole layout. `layout` overrides any of grid / hiddenCells / dayOrder.
  async function persist(state, { layout = {}, ...extra } = {}) {
    const data = { grid, hiddenCells, dayOrder, ...layout };
    await Promise.all([
      setDoc(doc(db, 'requests', requestId, 'itineraryMeta', 'skeletal'), {
        ...data, approvalState: state, savedAt: serverTimestamp(), ...extra,
      }),
      updateDoc(doc(db, 'requests', requestId), { skeletalApprovalState: state }),
    ]);
    setSavedAt(new Date());
  }

  async function handleSave() {
    setSaving(true);
    try {
      await persist(approvalState);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  }

  async function handleMarkApproved(note) {
    setSaving(true);
    try {
      await persist('approved', {
        approvedAt: serverTimestamp(),
        approvedBy: currentUser?.email || '',
        approvalNote: note || null,
      });
      setApprovalState('approved');
      setShowApprove(false);
      onApproved?.();
    } catch (err) {
      console.error('Failed to mark skeletal approved:', err);
      alert('Something went wrong saving the approval. Please try again.');
    } finally { setSaving(false); }
  }

  async function handleResetToEdit() {
    await persist('draft');
    setApprovalState('draft');
  }

  async function handleSaveAsTemplate() {
    const name = window.prompt('Name this skeletal template (e.g. "3-day Alicante football"):');
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      await addDoc(collection(db, 'skeletalTemplates'), {
        name: name.trim(),
        grid,
        hiddenCells,
        dayOrder,
        nDays,
        createdByEmail: currentUser?.email || '',
        publishedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to save skeletal template:', err);
      alert(`Failed to save template: ${err.message}`);
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handleApplyTemplate() {
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template || isLocked) return;
    if (!window.confirm(`Apply template "${template.name}"? This replaces the current skeletal layout.`)) return;
    setApplyingTemplate(true);
    try {
      // Map the template onto the current tour's day count: keep overlapping
      // days, leave any extra days blank, drop days beyond this tour's length.
      const inRange = (key) => {
        const dayIdx = parseInt(String(key).split('_')[0], 10);
        return !Number.isNaN(dayIdx) && dayIdx < nDays;
      };
      const newGrid = defaultGrid(nDays);
      Object.entries(template.grid || {}).forEach(([key, cell]) => { if (inRange(key)) newGrid[key] = cell; });
      const newHidden = {};
      Object.keys(template.hiddenCells || {}).forEach((key) => { if (inRange(key)) newHidden[key] = true; });
      const newOrder = {};
      Object.entries(template.dayOrder || {}).forEach(([day, keys]) => { if (inRange(day)) newOrder[day] = keys; });
      setGrid(newGrid);
      setHiddenCells(newHidden);
      setDayOrder(newOrder);
      await persist(approvalState, { layout: { grid: newGrid, hiddenCells: newHidden, dayOrder: newOrder } });
      setSelectedTemplateId('');
    } catch (err) {
      console.error('Failed to apply skeletal template:', err);
      alert(`Failed to apply template: ${err.message}`);
    } finally {
      setApplyingTemplate(false);
    }
  }

  async function handleDeleteTemplate() {
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template) return;
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'skeletalTemplates', template.id));
      setSelectedTemplateId('');
    } catch (err) {
      console.error('Failed to delete skeletal template:', err);
      alert(`Failed to delete template: ${err.message}`);
    }
  }

  async function applyFlightDetails() {
    const fd = request.flightDetails;
    if (!fd) return;
    const arrivalLabel = [
      fd.airline,
      fd.outboundFlight,
      fd.outboundTime && fd.arrivalTime ? `${fd.outboundTime} → ${fd.arrivalTime}` : (fd.outboundTime || fd.arrivalTime || ''),
    ].filter(Boolean).join(' · ') || 'Arrival Flight';
    const departureLabel = [
      fd.airline,
      fd.returnFlight,
      fd.returnTime && fd.returnArrivalTime ? `${fd.returnTime} → ${fd.returnArrivalTime}` : (fd.returnTime || fd.returnArrivalTime || ''),
    ].filter(Boolean).join(' · ') || 'Departure Flight';
    const lastDay = nDays - 1;
    const newGrid = { ...grid };
    newGrid[`0_arrival`]          = { label: arrivalLabel,   type: 'flight' };
    newGrid[`${lastDay}_departure`] = { label: departureLabel, type: 'flight' };
    setGrid(newGrid);
    setFlightsApplied(true);
    setSaving(true);
    try {
      await persist(approvalState, { layout: { grid: newGrid }, flightsApplied: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  }

  async function handleDownload() {
    setGeneratingPdf(true);
    try {
      const crest = await loadCrestForPdf(request.crestStoragePath, request.crestUrl);
      generateSkeletalPdf(grid, nDays, request, approvalState, crest, { hiddenCells, dayOrder });
    } catch (err) {
      console.error(err);
      alert('PDF generation failed. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  }

  function setDayKeys(dayIdx, keys) {
    setDayOrder((prev) => ({ ...prev, [dayIdx]: keys }));
  }

  function handleDrop(cellKey) {
    if (!dragRef.current || isLocked) return;
    const { label, type, sourceKey } = dragRef.current;
    setGrid((prev) => {
      const next = { ...prev };
      if (sourceKey) delete next[sourceKey];
      next[cellKey] = { label, type };
      return next;
    });
    dragRef.current = null;
    setDragOver(null);
  }

  // Adds an activity slot to a day — empty, or holding `item` (a palette item).
  function addActivity(dayIdx, item = null) {
    if (isLocked) return;
    const key = newActivitySlotKey();
    setDayKeys(dayIdx, addActivitySlot(daySlotKeys(dayOrder, grid, dayIdx), key));
    setGrid((prev) => {
      const next = { ...prev };
      if (item?.sourceKey) delete next[item.sourceKey];
      next[`${dayIdx}_${key}`] = item
        ? { label: item.label, type: item.type }
        : { label: slotLabel(key), type: 'slot', fixed: true };
      return next;
    });
  }

  function handleDropNew(dayIdx) {
    if (!dragRef.current || isLocked) return;
    addActivity(dayIdx, dragRef.current);
    dragRef.current = null;
    setDragOver(null);
  }

  // Removes an activity slot from a day altogether.
  function removeActivitySlot(dayIdx, key) {
    if (isLocked) return;
    setDayKeys(dayIdx, removeSlot(daySlotKeys(dayOrder, grid, dayIdx), key));
    const cellKey = `${dayIdx}_${key}`;
    setGrid((prev) => { const n = { ...prev }; delete n[cellKey]; return n; });
    setHiddenCells((prev) => { const n = { ...prev }; delete n[cellKey]; return n; });
  }

  // Takes the activity out of a slot, leaving the slot empty.
  function clearCell(cellKey) {
    if (isLocked) return;
    setGrid((prev) => { const n = { ...prev }; delete n[cellKey]; return n; });
  }

  function shiftLunch(dayIdx, dir) {
    if (isLocked) return;
    setDayKeys(dayIdx, moveLunch(daySlotKeys(dayOrder, grid, dayIdx), dir));
  }

  const approvalInfo = APPROVAL[approvalState] || APPROVAL.draft;
  const days = Array.from({ length: nDays }, (_, i) => i);
  const activityPalette = paletteItems.filter((item) => item.type !== 'flight');
  const fmtWhen = (d) => d?.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  function renderSlot(dayIdx, key, keys) {
    const cellKey = `${dayIdx}_${key}`;
    const cell    = grid[cellKey];
    const kind    = slotType(key);
    const isMeal  = kind === 'meal';
    const isLunch = key === 'lunch';
    const caption = slotLabel(key);
    const canDrop = !isMeal && !isLocked;
    const isOver  = dragOver === cellKey && canDrop;
    const dropProps = canDrop ? {
      onDragOver: (e) => { e.preventDefault(); setDragOver(cellKey); },
      onDragLeave: () => setDragOver(null),
      onDrop: () => handleDrop(cellKey),
    } : {};

    // Flight slots only appear on the first/last day, or where a flight is set
    if (kind === 'flight' && !cell) {
      const edgeDay = key === 'arrival' ? dayIdx === 0 : dayIdx === nDays - 1;
      if (!edgeDay || isLocked) return null;
      return (
        <div key={key} {...dropProps}
          className={`min-h-[30px] rounded-[8px] border-2 border-dashed flex items-center justify-center gap-1 text-[10.5px] transition ${isOver ? 'border-pb bg-blue-light/40 text-pb' : 'border-[#BBF7D0] text-[#86EFAC]'}`}>
          <IconFlight /> {caption} flight
        </div>
      );
    }

    if (hiddenCells[cellKey]) {
      if (isLocked) return null;
      return (
        <button key={key}
          onClick={() => setHiddenCells((p) => { const n = { ...p }; delete n[cellKey]; return n; })}
          className="w-full min-h-[22px] rounded-[6px] border border-dashed border-[#D1D5DB] text-[10px] text-[#9CA3AF] hover:border-pb/40 hover:text-pb transition"
          title="Add slot back"
        >+ {caption}</button>
      );
    }

    const lunchControls = isLunch && !isLocked && (
      <span className="flex items-center gap-0.5 shrink-0 ml-1 mr-2.5">
        {[[-1, '18 15 12 9 6 15', 'Move lunch earlier'], [1, '6 9 12 15 18 9', 'Move lunch later']].map(([dir, points, title]) => (
          <button key={dir} onClick={() => shiftLunch(dayIdx, dir)} disabled={!canMoveLunch(keys, dir)} title={title}
            className="w-5 h-5 flex items-center justify-center rounded text-amber-700 hover:bg-amber-100 disabled:opacity-25 disabled:hover:bg-transparent transition">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={points} />
            </svg>
          </button>
        ))}
      </span>
    );

    if (cell) {
      const cc = cellColors(cell.type);
      const isPlaceholder = cell.type === 'slot';
      const canDrag = !isMeal && !isPlaceholder && !isLocked;
      const showCaption = cell.type !== 'flight' && caption.toLowerCase() !== String(cell.label).toLowerCase();
      // × on a meal hides it (first/last day only); on a placeholder removes the
      // slot; on an activity clears it back to an empty slot.
      const removable = !isLocked && (!isMeal || dayIdx === 0 || dayIdx === nDays - 1);
      return (
        <div key={key} {...dropProps}
          draggable={canDrag}
          onDragStart={canDrag ? () => { dragRef.current = { label: cell.label, type: cell.type, sourceKey: cellKey }; } : undefined}
          className={`relative group rounded-[8px] px-2.5 py-1.5 min-h-[40px] flex items-center text-[11px] font-semibold leading-snug select-none ${isOver ? 'ring-2 ring-pb' : ''}`}
          style={{ background: cc.bg, border: `1.5px solid ${cc.border}`, color: cc.text, cursor: canDrag ? 'grab' : 'default' }}
        >
          {cell.type === 'flight' && <span className="mr-1 shrink-0"><IconFlight /></span>}
          <span className="flex-1 pr-3 min-w-0">
            {showCaption && <span className="block text-[9px] font-semibold uppercase tracking-wide opacity-60">{caption}</span>}
            <span className={isPlaceholder ? 'font-medium opacity-70' : ''}>{cell.label}</span>
          </span>
          {lunchControls}
          {removable && (
            <button
              onClick={() => (isMeal
                ? setHiddenCells((p) => ({ ...p, [cellKey]: true }))
                : isPlaceholder ? removeActivitySlot(dayIdx, key) : clearCell(cellKey))}
              className="absolute top-0.5 right-0.5 w-4 h-4 flex items-center justify-center text-[13px] opacity-0 group-hover:opacity-50 hover:!opacity-100 transition"
              style={{ color: cc.text }}
              title={isMeal ? 'Hide this meal' : isPlaceholder ? 'Remove this slot' : 'Clear this activity'}
            >×</button>
          )}
        </div>
      );
    }

    return (
      <div key={key} {...dropProps}
        className={`group/empty relative min-h-[40px] rounded-[8px] border-2 border-dashed flex items-center justify-center text-[11px] transition ${
          isOver  ? 'border-pb bg-blue-light/40 text-pb' :
          isMeal  ? 'border-transparent bg-amber-50 text-amber-500 italic font-medium' :
                    'border-[#E5E7EB] text-[#C4C9D1] hover:border-pb/30 hover:bg-blue-light/10'
        }`}
      >
        {isOver ? 'drop here' : caption}
        {lunchControls}
        {!isMeal && !isLocked && !isOver && (
          <button
            onClick={() => removeActivitySlot(dayIdx, key)}
            className="absolute top-0.5 right-0.5 w-4 h-4 flex items-center justify-center text-[11px] opacity-0 group-hover/empty:opacity-40 hover:!opacity-100 text-[#9CA3AF] hover:text-dr transition"
            title="Remove this slot"
          >×</button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Status + crest row */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
            style={{ background: approvalInfo.bg, color: approvalInfo.color }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: approvalInfo.color }}></span>
            {approvalInfo.label}
          </div>
          {savedAt && (
            <span className="text-[11px] text-[#9CA3AF]">
              Last saved {savedAt.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {savedAt && (
            <button onClick={handleDownload} disabled={generatingPdf}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-pb bg-blue-light border border-pb/20 rounded-[7px] hover:bg-pb hover:text-white transition disabled:opacity-60">
              <IconDownload /> {generatingPdf ? 'Generating…' : 'Download PDF'}
            </button>
          )}
        </div>
        {/* Club crest — managed from the Overview tab */}
        {request.crestUrl && (
          <div className="flex items-center gap-2">
            <img src={request.crestUrl} alt="Club crest" className="h-8 w-8 object-contain rounded border border-[#E5E7EB]" />
            <span className="text-[11px] text-[#9CA3AF]">Crest added via Overview tab</span>
          </div>
        )}
      </div>

      {/* Flight details banner */}
      {!isLocked && request.flightDetails && !flightsApplied && (request.flightDetails.outboundFlight || request.flightDetails.arrivalTime || request.flightDetails.returnFlight) && (
        <div className="flex items-center justify-between gap-3 p-4 bg-[#F0FDF4] border border-[#22C55E]/40 rounded-[12px] mb-4 flex-wrap">
          <div>
            <p className="text-[13px] font-bold text-[#14532D]">Flight details are available</p>
            <p className="text-[12px] text-[#166534] mt-0.5">
              {[request.flightDetails.airline, request.flightDetails.outboundFlight, request.flightDetails.returnFlight].filter(Boolean).join(' · ')} — auto-fill the arrival &amp; departure slots?
            </p>
          </div>
          <button
            onClick={applyFlightDetails}
            disabled={saving}
            className="px-4 py-2 bg-[#16A34A] text-white text-[12px] font-bold rounded-[8px] shrink-0 hover:bg-[#15803D] transition disabled:opacity-60"
          >
            Apply to itinerary
          </button>
        </div>
      )}

      {/* Sent under the old email sign-off, before staff marked approval themselves */}
      {(approvalState === 'pending_admin' || approvalState === 'pending_group') && (
        <div className="p-4 bg-amber-light border border-[#F59E0B] rounded-[12px] mb-4">
          <p className="text-[13px] font-bold text-[#92400E]">Sent for sign-off under the old email process</p>
          <p className="text-[12px] text-[#B45309] mt-0.5">Once the group has approved it, click “Mark as approved” below.</p>
        </div>
      )}
      {approvalState === 'approved' && (
        <div className="flex items-center justify-between gap-3 p-4 bg-green-light border border-[#6EE7B7] rounded-[12px] mb-4 flex-wrap">
          <div>
            <p className="text-[13px] font-bold text-[#065F46]">✓ Skeletal itinerary approved</p>
            <p className="text-[12px] text-[#047857] mt-0.5">
              {approval
                ? <>Marked approved by {approval.by}{approval.at ? ` on ${fmtWhen(approval.at)}` : ''}{approval.note ? ` — “${approval.note}”` : ''}</>
                : 'Ready to build the full itinerary.'}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={handleDownload} disabled={generatingPdf} className="flex items-center gap-1.5 px-4 py-2 bg-pb text-white text-[12px] font-bold rounded-[8px] hover:bg-navy transition disabled:opacity-60">
              <IconDownload /> {generatingPdf ? 'Generating…' : 'Download'}
            </button>
            <button onClick={handleResetToEdit} className="flex items-center gap-1.5 px-4 py-2 bg-white text-[#374151] border border-[#D1D5DB] text-[12px] font-bold rounded-[8px] hover:bg-surface transition">
              <IconEdit /> Edit
            </button>
          </div>
        </div>
      )}

      {/* Template picker */}
      {!isLocked && templates.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap p-3 bg-surface border border-[#E5E7EB] rounded-[10px]">
          <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Templates</span>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="flex-1 min-w-[180px] border border-[#E5E7EB] rounded-[8px] px-3 py-[7px] text-[12.5px] text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
          >
            <option value="">— Select a saved template —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.nDays}-day)</option>
            ))}
          </select>
          <button
            onClick={handleApplyTemplate}
            disabled={!selectedTemplateId || applyingTemplate}
            className="px-3.5 py-[7px] text-[12px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-50"
          >
            {applyingTemplate ? 'Applying…' : 'Apply'}
          </button>
          <button
            onClick={handleDeleteTemplate}
            disabled={!selectedTemplateId}
            className="px-3 py-[7px] text-[12px] font-semibold text-[#9CA3AF] hover:text-dr rounded-[8px] transition disabled:opacity-40"
          >
            Delete
          </button>
        </div>
      )}

      {/* Activity palette */}
      {!isLocked && (
        <div className="mb-5">
          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2.5">
            Activities — drag onto the grid
          </p>
          <div className="flex flex-wrap gap-2">
            {paletteItems.map((item) => {
              const cc = cellColors(item.type);
              return (
                <div
                  key={item.label}
                  draggable
                  onDragStart={() => { dragRef.current = { label: item.label, type: item.type, sourceKey: null }; }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-[8px] cursor-grab select-none transition hover:-translate-y-px hover:shadow-sm active:cursor-grabbing"
                  style={{ background: cc.bg, border: `1px solid ${cc.border}`, color: cc.text }}
                >
                  {item.type === 'flight' && <IconFlight />}
                  {item.label}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid — one column per day, each with its own slot order */}
      <div className="overflow-x-auto -mx-1 pb-1">
        <div className="flex gap-2 px-1 min-w-max">
          {days.map((dayIdx) => {
            const keys = daySlotKeys(dayOrder, grid, dayIdx);
            const newKey = `new_${dayIdx}`;
            return (
              <div key={dayIdx} className="flex-1 min-w-[132px] max-w-[200px] flex flex-col gap-1.5">
                <div className="text-center px-1 pb-1">
                  <div className="text-[12px] font-bold text-navy whitespace-nowrap">{dayLabel(request.departureDate, dayIdx)}</div>
                  <div className="text-[10px] font-medium text-[#9CA3AF]">Day {dayIdx + 1}</div>
                </div>
                {keys.map((key) => renderSlot(dayIdx, key, keys))}
                {!isLocked && (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(newKey); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={() => handleDropNew(dayIdx)}
                    className={`relative rounded-[8px] border-2 border-dashed transition ${dragOver === newKey ? 'border-pb bg-blue-light/40' : 'border-[#DCE6F5] hover:border-pb/40'}`}
                  >
                    <select
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        addActivity(dayIdx, v === '__empty' ? null : activityPalette.find((i) => i.label === v));
                      }}
                      className="w-full appearance-none bg-transparent text-center text-[11px] font-semibold text-pb py-2 cursor-pointer focus:outline-none"
                      title="Add an activity to this day (or drag one here)"
                    >
                      <option value="">{dragOver === newKey ? 'Drop to add' : '+ Add activity'}</option>
                      <option value="__empty">Empty slot</option>
                      {activityPalette.map((item) => <option key={item.label} value={item.label}>{item.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#F3F4F6] flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {approvalState !== 'approved' && (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saved && (
                <span className="flex items-center gap-1.5 text-[12px] font-semibold text-sg">
                  <IconCheck /> Saved
                </span>
              )}
              <button
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate}
                className="px-4 py-2.5 text-[13px] font-semibold text-pb bg-white border border-pb/30 hover:bg-pb/5 rounded-[9px] transition disabled:opacity-60"
              >
                {savingTemplate ? 'Saving…' : 'Save as template'}
              </button>
            </>
          )}
          {approvalState === 'approved' && (
            <button
              onClick={handleDownload}
              disabled={generatingPdf}
              className="flex items-center gap-2 px-5 py-2.5 text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60"
            >
              <IconDownload /> {generatingPdf ? 'Generating…' : 'Download Itinerary'}
            </button>
          )}
        </div>

        {approvalState !== 'approved' && (
          <button
            onClick={() => setShowApprove(true)}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-[13px] font-bold text-white bg-sg rounded-[9px] transition hover:opacity-90 disabled:opacity-60"
          >
            <IconCheck /> Mark as approved
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex gap-4 mt-4 flex-wrap">
        {[
          { color: '#F59E0B', bg: '#FFFBEB', label: 'Meals' },
          { color: '#22C55E', bg: '#F0FDF4', label: 'Flights' },
          { color: '#93C5FD', bg: '#EBF2FC', label: 'Activities' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5 text-[11px] text-[#9CA3AF]">
            <div className="w-2.5 h-2.5 rounded-[3px]" style={{ background: item.bg, border: `1.5px solid ${item.color}` }} />
            {item.label}
          </div>
        ))}
        <span className="text-[11px] text-[#9CA3AF]">· Drag from palette · Arrows on Lunch move it · × removes · “+ Add activity” adds a slot</span>
      </div>

      {showApprove && (
        <MarkApprovedModal saving={saving} onConfirm={handleMarkApproved} onClose={() => setShowApprove(false)} />
      )}
    </div>
  );
}

// ── Main Export ──────────────────────────────────────────────────────────────

export default function ItineraryBuilder({ requestId, request }) {
  const [view,             setView]             = useState('skeletal');
  const [skeletalApproved, setSkeletalApproved] = useState(false);

  // Check on mount whether the skeletal is already approved
  useEffect(() => {
    getDoc(doc(db, 'requests', requestId, 'itineraryMeta', 'skeletal')).then((snap) => {
      if (snap.exists() && snap.data().approvalState === 'approved') {
        setSkeletalApproved(true);
      }
    });
  }, [requestId]);

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Itinerary</h3>
        <div className="flex rounded-[8px] border border-[#E5E7EB] overflow-hidden">
          {[['skeletal', 'Skeletal'], ['full', 'Full Itinerary']].map(([key, label]) => {
            const locked = key === 'full' && !skeletalApproved;
            return (
              <button
                key={key}
                onClick={() => { if (!locked) setView(key); }}
                title={locked ? 'Complete and approve the skeletal itinerary first' : undefined}
                className={`px-4 py-2 text-[12px] font-medium transition ${view === key ? 'bg-pb text-white' : 'bg-white text-[#6B7280] hover:bg-surface'} ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {view === 'skeletal' && (
        <SkeletalItinerary
          requestId={requestId}
          request={request}
          onApproved={() => setSkeletalApproved(true)}
        />
      )}
      {view === 'full' && skeletalApproved && (
        <FullItineraryBuilder requestId={requestId} request={request} />
      )}
    </div>
  );
}
